function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useState } from 'react';
import { Checkbox, Spinner } from 'seagull';
import { Callout, ConfirmationModal, TextField, Typography } from 'seagull';
import { useStoreActions } from "../../contexts";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { howToEditAgreementUrl } from "../../constants";
import styles from "./AgreementTerminationModal.module.scss";
import { Controller, useForm } from 'react-hook-form';
import { connectControllerToTextField, getCustomMessageValidation } from "../../utils/react-hook-form.utils";
import { useRestfulWrapper } from "../useRestfulWrapper";
import { relationshipModel } from "../../models";
import { composeValidators, maxLengthValidation, noHtmlPatternValidation, requiredValidation } from 'validators';
const emailContentLabel = 'Email content';
const emailSubjectLabel = 'Email subject';
const emailContentRules = getCustomMessageValidation(emailContentLabel, true);
const emailSubjectRules = composeValidators([requiredValidation('Required'), maxLengthValidation(120, emailSubjectLabel), noHtmlPatternValidation(emailSubjectLabel)]);
function getDefaultEmailContent(buyerFirstName) {
  return "Dear ".concat(buyerFirstName, ",\n\nI am writing to formally notify you that, as per our previous discussions, I am terminating the agreement between us effective immediately.\n\nPlease note that all outstanding tasks and responsibilities have been completed to the best of our ability, and we're available to provide any necessary assistance during this transition period.\n\nWe sincerely appreciate the opportunity to have worked with you and wish you all the best in your future endeavors.");
}
function getDefaultEmailSubject(vendorName) {
  return "Your agreement with ".concat(vendorName, " was terminated");
}
function validateCustomerName(value, relationship) {
  const isValid = relationshipModel.customerName(relationship).trim().toLowerCase() === value.trim().toLowerCase();
  return isValid || "Company name doesn't match";
}
export function AgreementTerminationModal(props) {
  const {
    relationshipId,
    dismissModal
  } = props;
  const {
    api
  } = useStoreActions();
  const [notifyClient, setNotifyClient] = useState(false);
  const {
    control,
    setValue,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      companyName: '',
      emailSubject: '',
      emailContent: ''
    }
  });
  const {
    data,
    loading,
    renderRestfulState
  } = useRestfulWrapper({
    fetch: () => api.relationship.get(relationshipId)
  });
  useEffect(() => {
    if (data) {
      setValue('emailSubject', getDefaultEmailSubject(relationshipModel.vendorName(data)));
      setValue('emailContent', getDefaultEmailContent(relationshipModel.buyerFirstName(data) || ''));
    }
  }, [data]);
  return /*#__PURE__*/React.createElement(ConfirmationModal, {
    title: "Terminate agreement",
    skin: "danger",
    textAlignCenter: false,
    primaryButtonLabel: notifyClient ? 'Terminate and notify client' : 'Terminate',
    primaryButtonDisabled: loading,
    secondaryButtonLabel: "Cancel",
    onPrimaryButtonClick: handleSubmit(_ref => {
      let {
        emailSubject,
        emailContent
      } = _ref;
      return dismissModal({
        confirmed: true,
        notification: notifyClient ? {
          subject: emailSubject,
          message: emailContent
        } : undefined
      });
    }),
    onSecondaryButtonClick: () => dismissModal({
      confirmed: false
    }),
    content: renderRestfulState({
      renderLoader: () => /*#__PURE__*/React.createElement("div", {
        className: styles.loading
      }, /*#__PURE__*/React.createElement(Spinner, null)),
      renderChild: _ref2 => {
        let {
          data: relationship
        } = _ref2;
        return /*#__PURE__*/React.createElement("div", {
          className: styles.modalContentWrapper
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, null, "Terminating the agreement with", ' ', /*#__PURE__*/React.createElement(Typography, {
          className: styles.customerName,
          as: "span",
          weight: "bold"
        }, relationshipModel.customerName(relationship)), ' ', "will immediately stop new invoices issuance and the initiation of payments for pending invoices."), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Typography, null, "Note that this does not stop payments already in process."), /*#__PURE__*/React.createElement(Typography, null, "Please ensure all final invoices are settled before proceeding, as terminated agreements cannot be reactivated.")), /*#__PURE__*/React.createElement(Controller, {
          name: "companyName",
          control: control,
          rules: {
            validate: value => validateCustomerName(value, relationship)
          },
          render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
            label: "Confirm by entering client's company name",
            "data-testid": "company-name-verification"
          }))
        }), /*#__PURE__*/React.createElement(Callout, null, "An existing agreement can be edited to change the scope of work or terms.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
          openInNewTab: true,
          href: howToEditAgreementUrl
        }, "Learn more about editing an agreement")), /*#__PURE__*/React.createElement("div", {
          className: styles.notificationEmailFields
        }, /*#__PURE__*/React.createElement(Checkbox, {
          "data-testid": "toggle-notify",
          onChange: () => setNotifyClient(prev => !prev)
        }, /*#__PURE__*/React.createElement(Typography, null, "Notify my client of this action")), notifyClient && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Controller, {
          rules: emailSubjectRules,
          control: control,
          name: "emailSubject",
          render: p => /*#__PURE__*/React.createElement(TextField, _extends({
            label: emailSubjectLabel,
            testid: "email-subject"
          }, connectControllerToTextField(p, {
            alwaysShowErrors: true
          })))
        }), /*#__PURE__*/React.createElement(Controller, {
          rules: emailContentRules,
          control: control,
          name: "emailContent",
          render: p => /*#__PURE__*/React.createElement(TextField, _extends({
            multiline: true,
            minRows: 10,
            maxRows: 10,
            label: emailContentLabel,
            testid: "email-content"
          }, connectControllerToTextField(p)))
        }))));
      }
    })
  });
}